import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, RefreshCw, Check, AlertTriangle, Activity, Clock, Lock } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, Spinner } from "../components/common";
import guardLogo from "../assets/account-guard-logo.png";

const FEATURES = [
  { icon: Activity, title: "24/7 monitoring", body: "We watch your account around the clock, every day." },
  { icon: AlertTriangle, title: "Fraud & suspicious activity", body: "Spot risky transfers, failed charges and odd spending." },
  { icon: Lock, title: "Login & device alerts", body: "Know when a new or untrusted device signs in." },
  { icon: Sparkles, title: "AI risk briefing", body: "A clear, plain-English safety report, refreshed anytime." },
];

const LEVEL_STYLE = {
  low: { label: "Low risk", cls: "bg-success/10 text-success border-success/30", dot: "bg-success" },
  medium: { label: "Medium risk", cls: "bg-amber-500/10 text-amber-600 border-amber-500/30", dot: "bg-amber-500" },
  high: { label: "High risk", cls: "bg-destructive/10 text-destructive border-destructive/30", dot: "bg-destructive" },
};

function SeverityPill({ severity }) {
  const map = { low: "bg-success/10 text-success", medium: "bg-amber-500/10 text-amber-600", high: "bg-destructive/10 text-destructive" };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${map[severity] || map.low}`}>{severity}</span>;
}

export default function AccountGuard() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = async () => {
    try { const { data } = await api.get("/guard/status"); setStatus(data); return data; }
    catch (e) { toast.error(errMsg(e)); }
  };
  const loadBriefing = async () => {
    try { const { data } = await api.get("/guard/briefing"); setBriefing(data); }
    catch (e) { /* not active */ }
  };

  useEffect(() => { (async () => { const s = await loadStatus(); if (s?.subscribed) { loadBriefing(); requestPush(); } })(); }, []);

  const requestPush = () => {
    try {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch {}
  };

  const subscribe = () => nav("/app/badge");

  const cancel = () => nav("/app/badge");

  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post("/guard/briefing/refresh"); setBriefing(data); toast.success("Briefing updated"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setRefreshing(false); }
  };

  if (!status) return <div className="py-16 grid place-items-center"><Spinner className="w-6 h-6 text-primary" /></div>;

  const sub = status.subscription;

  return (
    <div className="animate-fade-up" data-testid="account-guard-page">
      <PageHeader title="Account Guard" subtitle="AI that watches your account 24/7" />

      {!status.subscribed ? (
        <>
          <div className="rounded-3xl p-6 mb-5 text-center text-white relative overflow-hidden"
            style={{ background: "linear-gradient(150deg,#4B23CC,#2b1570)" }}>
            <img src={guardLogo} alt="Account Guard" className="w-28 h-28 mx-auto mb-3 drop-shadow-xl" data-testid="guard-hero-logo" />
            <h2 className="font-display text-2xl font-semibold">Stay a step ahead of fraud</h2>
            <p className="text-white/85 text-sm mt-2 max-w-xs mx-auto">Let our AI keep an eye on your money, your logins and your devices — all day, every day.</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-2">
              <span className="text-white/90 text-sm font-semibold">Included in Jack Cash Plus · $4.99/mo</span>
            </div>
            {!status.trial_used && <p className="text-white/90 text-xs mt-2 font-semibold">{status.trial_days}-day free trial · cancel anytime</p>}
          </div>

          <div className="space-y-3 mb-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex items-start gap-3 rounded-2xl bg-card border border-border p-4">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"><f.icon size={18} /></span>
                <div><div className="font-semibold text-sm">{f.title}</div><div className="text-xs text-muted-foreground">{f.body}</div></div>
              </div>
            ))}
          </div>

          <button data-testid="guard-subscribe-btn" onClick={() => nav("/app/badge")}
            className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2">
            <ShieldCheck size={18} /> Get Jack Cash Plus
          </button>
          <p className="text-xs text-muted-foreground text-center mt-2">Account Guard comes with Jack Cash Plus — one plan, one place to pay.</p>
        </>
      ) : (
        <>
          <div className="rounded-3xl p-5 mb-5 text-white relative overflow-hidden" style={{ background: "linear-gradient(150deg,#4B23CC,#2b1570)" }}>
            <div className="flex items-center gap-3">
              <img src={guardLogo} alt="Account Guard" className="w-12 h-12 drop-shadow" />
              <div>
                <div className="font-display text-lg font-semibold flex items-center gap-2">Account Guard is active <Check size={16} /></div>
                <div className="text-white/80 text-xs mt-0.5">
                  {sub?.status === "trialing" ? `Free trial · ends ${(sub?.trial_end || "").slice(0, 10)}` :
                    sub?.cancel_at_period_end ? `Ends ${(sub?.current_period_end || "").slice(0, 10)}` :
                      `Renews ${(sub?.current_period_end || "").slice(0, 10)} · ${status.price_display}/mo`}
                </div>
              </div>
            </div>
          </div>

          {briefing ? (
            <div className="rounded-3xl bg-card border border-border p-5 mb-4" data-testid="guard-briefing">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-semibold flex items-center gap-2"><Sparkles size={18} className="text-primary" /> AI Risk Briefing</h2>
                <button data-testid="guard-refresh-btn" onClick={refresh} disabled={refreshing}
                  className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-primary active:scale-90 transition-transform">
                  {refreshing ? <Spinner className="w-4 h-4" /> : <RefreshCw size={16} />}
                </button>
              </div>

              {(() => { const lv = LEVEL_STYLE[briefing.briefing?.risk_level] || LEVEL_STYLE.low; return (
                <div className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 mb-3 ${lv.cls}`} data-testid="guard-risk-level">
                  <span className={`w-2 h-2 rounded-full ${lv.dot}`} /><span className="text-xs font-bold">{lv.label}</span>
                </div>
              ); })()}

              <div className="font-display text-lg font-semibold">{briefing.briefing?.headline}</div>
              <p className="text-sm text-muted-foreground mt-1">{briefing.briefing?.summary}</p>

              <div className="mt-4 space-y-2">
                {(briefing.briefing?.findings || []).map((f, i) => (
                  <div key={i} className="rounded-2xl bg-secondary/50 p-3">
                    <div className="flex items-center justify-between"><span className="font-semibold text-sm">{f.title}</span><SeverityPill severity={f.severity} /></div>
                    <p className="text-xs text-muted-foreground mt-1">{f.detail}</p>
                  </div>
                ))}
              </div>

              {(briefing.briefing?.recommendations || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Recommended</div>
                  <ul className="space-y-1.5">
                    {briefing.briefing.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm"><Check size={15} className="text-success mt-0.5 shrink-0" />{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-4"><Clock size={12} /> Updated {(briefing.generated_at || "").replace("T", " ").slice(0, 16)} UTC</div>
            </div>
          ) : (
            <div className="rounded-3xl bg-card border border-border p-6 mb-4 text-center">
              <Spinner className="w-6 h-6 text-primary mx-auto" /><p className="text-sm text-muted-foreground mt-2">Preparing your first briefing…</p>
            </div>
          )}

          {!sub?.cancel_at_period_end && (
            <button data-testid="guard-cancel-btn" onClick={() => nav("/app/badge")}
              className="w-full h-12 rounded-2xl bg-secondary font-semibold text-sm text-muted-foreground">
              Manage in Jack Cash Plus
            </button>
          )}
        </>
      )}
    </div>
  );
}
